import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
    const adminData = sessionStorage.getItem('adminkhadamat');
    if (adminData) {
        try {
            const parsed = JSON.parse(adminData);
            // البحث عن التوكن في أماكن مختلفة
            if (parsed.data?.token?.access_token) {
              const token = String(parsed.data.token.access_token);
              return token;
            }
            if (parsed.data?.access_token) return parsed.data.access_token;
            if (parsed.data?.token) return parsed.data.token;
            if (parsed.token) return parsed.token;
            if (parsed.result?.token) return parsed.result.token;
            if (parsed.user?.token) return parsed.user.token;
            return null;
        } catch {
            return null;
        }
    }
    return null;
};

// Fetch all pharmacies
export const fetchPharmacies = createAsyncThunk(
    'adminPharmacies/fetchPharmacies',
    async ({ page = 1, name = '' }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const params = { page };
            if (name) params.name = name;

            const response = await axios.get(`${BASE_URL}/admin/pharmacies`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
                params
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب الصيدليات');
        }
    }
);

// Fetch pharmacy details
export const fetchPharmacyDetails = createAsyncThunk(
    'adminPharmacies/fetchPharmacyDetails',
    async (pharmacyId, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/pharmacies/${pharmacyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل الصيدلية');
        }
    }
);

// Toggle pharmacy ban status
export const togglePharmacyBan = createAsyncThunk(
    'adminPharmacies/togglePharmacyBan',
    async ({ pharmacyId, status }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/pharmacies/${pharmacyId}/ban?ban=${status}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في العملية');
        }
    }
);

// Toggle pharmacy activation status
export const togglePharmacyStatus = createAsyncThunk(
    'adminPharmacies/togglePharmacyStatus',
    async ({ pharmacyId, status }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/pharmacies/${pharmacyId}/status?status=${status}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في العملية');
        }
    }
);

// Fetch pharmacy branches
export const fetchPharmacyBranches = createAsyncThunk(
    'adminPharmacies/fetchPharmacyBranches',
    async ({ pharmacyId, page = 1 }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/pharmacies/${pharmacyId}/branches`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
                params: { page }
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب الفروع');
        }
    }
);

// Fetch branch details
export const fetchPharmacyBranchDetails = createAsyncThunk(
    'adminPharmacies/fetchPharmacyBranchDetails',
    async ({ pharmacyId, branchId }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/pharmacies/${pharmacyId}/branches/${branchId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل الفرع');
        }
    }
);

const adminPharmaciesSlice = createSlice({
    name: 'adminPharmacies',
    initialState: {
        pharmacies: [],
        pharmacyDetails: null,
        branches: [],
        branchDetails: null,
        pagination: null,
        branchesPagination: null,
        loading: false,
        detailsLoading: false,
        branchesLoading: false,
        branchDetailsLoading: false,
        updating: false,
        error: null,
        success: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = null;
        },
        clearPharmacyDetails: (state) => {
            state.pharmacyDetails = null;
        },
        clearBranchDetails: (state) => {
            state.branchDetails = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Pharmacies
            .addCase(fetchPharmacies.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPharmacies.fulfilled, (state, action) => {
                state.loading = false;
                const pharmaciesData = action.payload.data?.data || action.payload.data;
                state.pharmacies = Array.isArray(pharmaciesData) ? pharmaciesData : [];
                state.pagination = action.payload.data?.meta || action.payload.pagination;
            })
            .addCase(fetchPharmacies.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch Pharmacy Details
            .addCase(fetchPharmacyDetails.pending, (state) => {
                state.detailsLoading = true;
                state.error = null;
            })
            .addCase(fetchPharmacyDetails.fulfilled, (state, action) => {
                state.detailsLoading = false;
                state.pharmacyDetails = action.payload.data;
            })
            .addCase(fetchPharmacyDetails.rejected, (state, action) => {
                state.detailsLoading = false;
                state.error = action.payload;
            })

            // Toggle Pharmacy Ban
            .addCase(togglePharmacyBan.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(togglePharmacyBan.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم تحديث حالة الحظر بنجاح';
            })
            .addCase(togglePharmacyBan.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            // Toggle Pharmacy Status
            .addCase(togglePharmacyStatus.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(togglePharmacyStatus.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم تحديث حالة التفعيل بنجاح';
            })
            .addCase(togglePharmacyStatus.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            // Fetch Pharmacy Branches
            .addCase(fetchPharmacyBranches.pending, (state) => {
                state.branchesLoading = true;
                state.error = null;
            })
            .addCase(fetchPharmacyBranches.fulfilled, (state, action) => {
                state.branchesLoading = false;
                const branchesData = action.payload.data?.data || action.payload.data;
                state.branches = Array.isArray(branchesData) ? branchesData : [];
                state.branchesPagination = action.payload.data?.meta || action.payload.pagination;
            })
            .addCase(fetchPharmacyBranches.rejected, (state, action) => {
                state.branchesLoading = false;
                state.error = action.payload;
            })

            // Fetch Branch Details
            .addCase(fetchPharmacyBranchDetails.pending, (state) => {
                state.branchDetailsLoading = true;
                state.error = null;
            })
            .addCase(fetchPharmacyBranchDetails.fulfilled, (state, action) => {
                state.branchDetailsLoading = false;
                state.branchDetails = action.payload.data;
            })
            .addCase(fetchPharmacyBranchDetails.rejected, (state, action) => {
                state.branchDetailsLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, clearSuccess, clearPharmacyDetails, clearBranchDetails } = adminPharmaciesSlice.actions;
export default adminPharmaciesSlice.reducer;

